import { useState } from "react"
import { Radio, Edit, X, Trash2, UserMinus } from "lucide-react"
import type { Broadcast } from "./types"
import { EditBroadcastPage } from "./edit-broadcast-page"
import { AvatarPhoto } from "./avatar-photo"

/**
 * Broadcast-list info: recipients + rename + delete. Backed by `sdk.broadcasts`
 * through PresencePage — the list is owner-private, so every action here is the
 * owner acting on their own list. Recipients are NOT members: removing one only
 * stops future sends; the DM and its history stay.
 */
interface BroadcastInfoPanelProps {
  broadcast: Broadcast
  onClose: () => void
  onRename?: (name: string) => void
  onDelete?: () => void
  onRemoveRecipient?: (userId: string) => void
}

export function BroadcastInfoPanel({ broadcast, onClose, onRename, onDelete, onRemoveRecipient }: BroadcastInfoPanelProps) {
  const recipients = broadcast.recipients || []
  const [editing, setEditing] = useState(false)

  if (editing) {
    return (
      <EditBroadcastPage
        broadcastId={broadcast.id}
        currentName={broadcast.name}
        onClose={() => setEditing(false)}
        onSave={({ name }) => { onRename?.(name); setEditing(false) }}
      />
    )
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <h3 className="text-base font-semibold text-foreground">Broadcast info</h3>
        <div className="flex items-center gap-1">
          <button onClick={() => setEditing(true)} className="p-2 rounded-full hover:bg-muted text-primary" title="Edit broadcast">
            <Edit className="w-4.5 h-4.5" />
          </button>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-muted text-muted-foreground" title="Close">
            <X className="w-4.5 h-4.5" />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto">
        {/* Avatar and name */}
        <div className="flex flex-col items-center py-6 px-4">
          <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mb-3">
            <Radio className="w-10 h-10 text-primary" />
          </div>
          {/* Same fix as the group info header: an unbroken 100-char name has
              no space to wrap at, so `max-w-xs` alone still let it overflow
              the box and widen the panel's scroll area. */}
          <h2 className="text-lg font-medium text-foreground text-center max-w-xs min-w-0 [overflow-wrap:anywhere]">{broadcast.name}</h2>
          <p className="text-sm text-muted-foreground mt-1">{broadcast.recipientCount} recipients</p>
          <p className="text-xs text-muted-foreground mt-2 text-center max-w-xs">
            Each recipient receives your message as an ordinary private chat. They can't see
            the list or the other recipients, and their replies come back to you privately.
          </p>
        </div>

        {/* Recipients */}
        <div className="mx-4 mb-3 rounded-2xl border border-border bg-card overflow-hidden">
          <div className="px-4 py-2.5 border-b border-border">
            <p className="text-sm font-medium text-foreground">Recipients ({recipients.length})</p>
          </div>
          {recipients.map((recipient) => (
            <div key={recipient.id} className="group flex items-center gap-3 px-4 py-2.5 hover:bg-muted/50">
              <div className="w-9 h-9 rounded-full bg-muted overflow-hidden">
                <AvatarPhoto
                  src={recipient.imageUrl}
                  alt={recipient.name}
                  className="w-full h-full object-cover"
                  fallback={
                    <div className="w-full h-full flex items-center justify-center bg-muted text-xs font-medium text-muted-foreground">
                      {recipient.name[0]?.toUpperCase() || "?"}
                    </div>
                  }
                  contactId={recipient.id}
                />
              </div>
              <span className="flex-1 text-sm text-foreground truncate">{recipient.name}</span>
              {onRemoveRecipient && (
                <button
                  onClick={() => onRemoveRecipient(recipient.id)}
                  className="p-1.5 rounded-full text-muted-foreground opacity-0 group-hover:opacity-100 hover:bg-destructive/10 hover:text-destructive transition-opacity"
                  title={`Remove ${recipient.name}`}
                >
                  <UserMinus className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
          {recipients.length === 0 && (
            <p className="px-4 py-3 text-xs text-muted-foreground">No recipients left on this list.</p>
          )}
        </div>

        {/* Danger zone */}
        <div className="mx-4 mb-6 rounded-2xl border border-border bg-card overflow-hidden">
          <button
            onClick={onDelete}
            className="w-full flex items-center justify-center px-4 py-3 hover:bg-muted/50 transition-colors text-red-500"
          >
            <div className="flex items-center gap-2">
              <Trash2 className="w-4 h-4" />
              <span className="text-sm font-normal">Delete broadcast</span>
            </div>
          </button>
        </div>
      </div>
    </div>
  )
}
